import { Close, Save } from '@mui/icons-material';
import {
  Divider,
  Switch,
  FormControlLabel,
  Box,
  Button,
  Fade,
  Grid,
  MenuItem,
  TextField,
  Typography,
  Alert
} from '@mui/material';
import { kebab } from 'case';
import { deepEqual } from 'fast-equals';
import { Form, Formik } from 'formik';
import { useSnackbar } from 'notistack';
import { FC, useContext, useState } from 'react';
import * as Yup from 'yup';
import { useHistory } from 'react-router-dom';
// Components
import { Link, Loader, Modal, SMBSelect } from '../../../components';
import { createCustomPage, getCustomPage } from '../../../fetch';
import { ICustomPage, ICustomPageDetail } from '../../../models';
import { UserContext } from '../../../context';
import { useFlags } from '../../../hooks';

interface IAddCustomPageModal {
  open: boolean;
  onClose: () => void;
  customPages: ICustomPage[];
  fetchCustomPages: () => void;
}

const AddCustomPageSchema = Yup.object().shape({
  name: Yup.string().required('Required').max(255, 'Max 255 characters'),
  slug: Yup.string(),
  isActive: Yup.boolean()
});

export const AddCustomPageModal: FC<IAddCustomPageModal> = ({
  customPages,
  open,
  onClose,
  fetchCustomPages
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const { authorizeUserNavigationAccess } = useFlags();
  const { isAdmin } = useContext(UserContext);
  const [createdSlug, setCreatedSlug] = useState<string>('');
  return (
    <>
      <Formik
        enableReinitialize={true}
        initialValues={{
          name: '',
          slug: '',
          isActive: true,
          customPageId: ''
        }}
        validationSchema={AddCustomPageSchema}
        onSubmit={async (values, actions) => {
          try {
            let existingData: ICustomPageDetail | null = null;

            // take all of the sections from an existing page and then create a new page with the copied data
            if (values.customPageId) {
              // pass negative one to load all of the data sub-sections
              existingData = await getCustomPage(values.customPageId, -1);
            }
            const res = await createCustomPage({
              name: values.name,
              slug: values.slug ? kebab(values.slug) : kebab(values.name),
              isActive: values.isActive,
              sections: existingData?.sections || [],
              navigationType: 'None',
              customNavigationId: null
            });
            if (res?.Detail) {
              enqueueSnackbar(res.Detail, {
                variant: 'error'
              });
            } else if (res) {
              // custom page id is returned if we get a successful page creation
              const page = await getCustomPage(res, 0);
              if (page?.customPageId) {
                if (authorizeUserNavigationAccess) {
                  setCreatedSlug(page.slug);
                  actions.resetForm();
                } else {
                  enqueueSnackbar('Custom Page Created!', {
                    variant: 'success'
                  });
                  history.push(`/admin/manage-content/custom-pages/${page.slug}`);
                }
                fetchCustomPages();
              }
            }
          } catch (error) {
            console.log(error);
            if (error?.response?.data?.Detail) {
              enqueueSnackbar(error.response.data.Detail, {
                variant: 'error'
              });
              // Fallback case if no error is provided.
            } else {
              enqueueSnackbar('Custom page was not created', {
                variant: 'error'
              });
            }
          }
        }}
      >
        {({
          resetForm,
          isSubmitting,
          values,
          initialValues,
          setFieldValue,
          handleSubmit,
          dirty,
          isValid,
          handleBlur
        }) => {
          return (
            <Modal
              open={open}
              onClose={() => {
                if (!deepEqual(initialValues, values)) {
                  const result = window.confirm(
                    'You have unsaved changes, are you sure you want to exit?'
                  );
                  if (result) {
                    resetForm();
                    setCreatedSlug('');
                    onClose();
                  } else {
                    return;
                  }
                } else {
                  setCreatedSlug('');
                  onClose();
                }
              }}
              maxWidth="xs"
            >
              {isSubmitting && <Loader type="overlay" position="centered" />}
              <Fade in={open}>
                <Form onSubmit={handleSubmit} autoComplete="none">
                  <Box marginBottom="0.5rem">
                    <Typography variant="h5">Add Custom Page</Typography>
                  </Box>
                  <Box marginBottom="0.5rem">
                    <Divider />
                  </Box>
                  {createdSlug && authorizeUserNavigationAccess && (
                    <Alert severity="success">
                      Custom Page created. Edit your page{' '}
                      <Link to={`/admin/manage-content/custom-pages/${createdSlug}`}>here</Link>.
                      Your page will not be accessible until a user applies a role to the page.
                      {isAdmin && ` To do this, click `}
                      {isAdmin && (
                        <Link
                          to={`/admin/user-tools/unified-user-management/manage-roles-and-titles`}
                        >
                          here
                        </Link>
                      )}
                    </Alert>
                  )}
                  <Grid container spacing={1}>
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            color="primary"
                            checked={values.isActive}
                            id="active-inactive"
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                              checked: boolean
                            ) => {
                              setFieldValue('isActive', checked);
                            }}
                          />
                        }
                        label="Active"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        variant="outlined"
                        autoComplete="nope"
                        label="Name"
                        name="name"
                        value={values.name}
                        onBlur={handleBlur}
                        size="small"
                        onChange={e => {
                          setFieldValue('name', e.target.value);
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        variant="outlined"
                        autoComplete="nope"
                        label="Slug"
                        name="slug"
                        value={values.slug}
                        onBlur={handleBlur}
                        size="small"
                        onChange={e => setFieldValue('slug', e.target.value)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <SMBSelect
                        variant="outlined"
                        onChange={e => {
                          setFieldValue('customPageId', e.target.value);
                        }}
                        handleClear={() => setFieldValue('customPageId', '')}
                        name={`customPageId`}
                        id={`personTypeId`}
                        label="Copy Existing Page"
                        value={values.customPageId}
                      >
                        {customPages &&
                          customPages.map((page: ICustomPage, index) => {
                            return (
                              <MenuItem key={`${index}`} value={page.customPageId}>
                                {page.name}
                              </MenuItem>
                            );
                          })}
                      </SMBSelect>
                    </Grid>
                  </Grid>
                  <Box marginTop="2rem">
                    <Button
                      disabled={!dirty || isSubmitting || !isValid}
                      type="submit"
                      startIcon={<Save />}
                      variant="contained"
                      color="primary"
                    >
                      Save
                    </Button>
                    <Button
                      type="button"
                      variant="contained"
                      color="inherit"
                      startIcon={<Close />}
                      onClick={() => {
                        if (!deepEqual(initialValues, values)) {
                          const result = window.confirm(
                            'You have unsaved changes, are you sure you want to exit?'
                          );
                          if (result) {
                            resetForm();
                            onClose();
                          } else {
                            return;
                          }
                        } else {
                          onClose();
                        }
                      }}
                    >
                      Cancel
                    </Button>
                  </Box>
                </Form>
              </Fade>
            </Modal>
          );
        }}
      </Formik>
    </>
  );
};
